import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Switch } from '@headlessui/react'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PaginationControls from '../../components/PaginationControls';
import { createMembre, deleteMembre, getAllMembres, updateMembre } from '../../API/Crud/Membre';
import ConfirmationModal from '../../components/ConfirmationModal';
import DashboardTemplate from '../template/DashboardTemplate';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { get, post, upload } from '../../services/ApiRouting';
import { useDropzone } from "react-dropzone";
import { BiEdit, BiTrash } from 'react-icons/bi';

function Member() {
  const [Members, setMembers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [selectedMember, setselectedMember] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });
  const { register, handleSubmit, control, reset, formState: { errors }, clearErrors } = useForm({
    defaultValues: {
      name: "",
      title: "",
      email: "",
      file: "",
    }
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false); // State to control confirmation modal visibility
  const [memberToDelete, setMemberToDelete] = useState(null); // State to hold the Service to be deleted

  // Pagination logic
  const totalMembers = Members?.length;
  const indexOfLastItem = totalMembers - (currentPage - 1) * itemsPerPage;

  const paginate = (pageNumber) => fetchMembers(pageNumber);

  useEffect(() => {
    const storedToken = sessionStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
    } else {
      console.error('No token found');
      setError('No token found. Please log in.');
    }
  }, []);

  async function fetchMembers(number) {
    setLoading(true);
    try {
      const data = await getAllMembres(number);
      setMembers(data.data);
      setPagination({
        currentPage: data.current_page,
        perPage: data.per_page,
        total: data.total,
        lastPage: data.last_page,
      })
      setError(null);
    } catch (error) {
      setError(error.message || 'An unknown error occurred');
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!token) return;

    fetchMembers(1);
  }, [token]);



  const handleDeleteMember = async (MemberId) => {
    setLoading(true);
    try {
      const response = await deleteMembre(MemberId);
      if (response.ok) {
        setMembers((prevMembers) => prevMembers.filter(member => member.id !== MemberId));
        toast.success('Member deleted successfully');
      }
    } catch (error) {
      setError('Error deleting member');
      toast.error('Failed to delete member');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMember) {
      reset({
        name: selectedMember.name || "",
        title: selectedMember.title || "",
        email: selectedMember.email || "",
        contributer: selectedMember.contributer || false,
        file: selectedMember.image,
      });
    }
  }, [selectedMember, reset]);

  const handleEditMember = (member) => {
    setselectedMember(member); // triggers useEffect above

    setFormData({
      id: member.id,
      name: member.name,
      title: member.title,
      email: member.email,
      contributer: member.contributer,
      file: member.image,
    });
    let imagePath = "";
    try {
      const parsed = Array.isArray(member.image) ? member.image : JSON.parse(member.image); // parsed = ["/storage/uploads/services/..."]
      imagePath = Array.isArray(parsed) ? parsed[0] : parsed;          // take first element
    } catch (e) {
      console.error("Invalid JSON:", e);
    }
    setUploadedFiles(imagePath);
    setShowEditModal(true);

  };

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    title: '',
    email: '',
    contributer: false,
    file: '',
  });
  const [uploadedFiles, setUploadedFiles] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  // Upload function
  const uploadFiles = async (files, field) => {
    setIsUploading(true);
    const uploadPromises = files.map(async (file) => {
      try {
        const formData = new FormData();
        formData.append('file', file, file.name);

        const response = await upload(`${API_URL}/crud/members/fileUpload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const serverFilePath = response.fileId || response.data.file_path;
        return serverFilePath;
      } catch (err) {
        console.error("Upload error:", err);
        return null;
      }
    });

    const serverIds = await Promise.all(uploadPromises);
    const validServerIds = serverIds.filter(Boolean);

    if (validServerIds.length > 0) {
      // Member only has one image, use the last valid uploaded file
      const fileId = validServerIds[validServerIds.length - 1];
      setUploadedFiles(fileId);
      field.onChange(fileId);
    }

    setIsUploading(false);
  };

  // Remove file function
  const removeFile = (field) => {
    setUploadedFiles(null);
    if (field) field.onChange(null);
  };

  // Dropzone uploader
  const onDrop = async (acceptedFiles) => {
    let uploadedPaths = "";

    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append("file", file);

      try {

        const response = await upload(`${API_URL}/crud/members/fileUpload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // get server file path
        const serverPath = response.data.fileId || response.data.file_path;
        uploadedPaths = serverPath;
      } catch (err) {
        console.error("Upload failed:", err);
      }
    }

    setUploadedFiles(uploadedPaths);
  };


  const onCreateSubmit = async (data) => {
    const memberData = {
      ...data,
      file: uploadedFiles, // array of server paths
    };
    try {
      const response = await createMembre(memberData);  // pass token + form data
      if (response.ok) {
        setShowModal(false);
        toast.success('Membre created successfully');
        setMembers((prevMembers) => [...prevMembers, response.data]);
        setFormData({
          id: null,
          name: '',
          email: '',
          file: '',
        })
        reset()
        reset({}, { keepErrors: false, keepDirty: false, keepTouched: false });
        setUploadedFiles()
      }
    } catch (error) {
      console.error("Error creating member:", error);
    }
  };

  const onUpdateSubmit = async (data) => {
    const memberData = {
      ...data,
      file: uploadedFiles, // array of server paths
    };
    try {
      const response = await updateMembre(selectedMember.id, memberData);  // pass token + form data
      if (response.ok) {
        fetchMembers(1);
        setShowEditModal(false);
        setselectedMember(null);
        reset({
          name: "",
          title: "",
          email: "",
          file: "",
        });
        setUploadedFiles(null);
        toast.success('Membre mette à jour avec succèss ');
      }
    } catch (error) {
      console.error("Error creating member:", error);
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const baseUrl = `${API_PUBLIC_URL}`;


  const handleConfirmDelete = () => {
    if (memberToDelete) {
      handleDeleteMember(memberToDelete.id);
      setShowConfirmModal(false);
      setMemberToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setMemberToDelete(null);
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setselectedMember(null);
    reset({
      name: '',
      title: '',
      email: '',
      file: '',
    });
    setUploadedFiles(null);
  };

  const handleOpenCreateModal = () => {
    setselectedMember(null);
    reset({
      name: '',
      title: '',
      email: '',
      file: '',
    });
    setUploadedFiles(null);
    setShowModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setselectedMember(null);
    reset({
      name: '',
      title: '',
      email: '',
      file: '',
    });
    setUploadedFiles(null);
  };



  return (
    <DashboardTemplate>
      <div className="flex flex-col">
        <ToastContainer />
        <div className="transition-all duration-500 flex-1">
          <div className="content p-2 md:p-5">
            {loading ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-10 animate-spin fill-orange-600 block mx-auto"
                viewBox="0 0 24 24">
                <path
                  d="M12 22c5.421 0 10-4.579 10-10h-2c0 4.337-3.663 8-8 8s-8-3.663-8-8c0-4.336 3.663-8 8-8V2C6.579 2 2 6.58 2 12c0 5.421 4.579 10 10 10z"
                  data-original="#000000" />
              </svg>
            ) : error ? (
              <p>Error: {error}</p>
            ) : (
              <div className="font-[sans-serif]">
                <div className="flex justify-between items-center mb-4">
                  <h1 className="text-2xl font-bold text-gray-900">Membres</h1>
                  <button className="bg-[#F2A549] font-normal cursor-pointer text-black px-4 py-2 rounded hover:bg-[#efb46f]" onClick={handleOpenCreateModal}>
                    Add a member
                  </button>
                </div>
                <div className='w-auto max-w-full overflow-x-auto'>
                  <table className="border-collapse min-w-full  border border-gray-300 rounded-lg">
                    <thead className="bg-[#F2A549] text-white">
                      <tr className='text-center'>
                        <th className="p-4  font-normal text-black">Image</th>
                        <th className="p-4  font-normal text-black">Name</th>
                        <th className="p-4  font-normal text-black">Email</th>
                        <th className="p-4  font-normal text-black">Title</th>
                        <th className="p-4  font-normal text-black">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(Members) && Members.map((member) => {
                        let imagePath = "";
                        try {
                          const parsed = JSON.parse(member.image);
                          console.log(parsed);
                          imagePath = Array.isArray(parsed) ? parsed[0] : parsed;          // take first element
                        } catch (e) {
                          // console.error("Invalid JSON:", e);
                          imagePath = member.image
                        }
                        return (
                          <tr className=" border border-gray-300" key={member.id}>
                            <td className=" border border-gray-300 py-2 ">
                              {imagePath !== "null"
                                ? (<img src={baseUrl + imagePath} alt={member.name} className="w-20 h-20 mx-auto object-cover rounded-full" />)
                                : (<img src="/images/LOGO.png" alt={member.name} className="w-20 h-20 mx-auto object-cover border rounded-full" />)}

                            </td>
                            <td className=" text-center text-sm border border-gray-300">{member.name}</td>
                            <td className="relative w-[20%] border border-gray-300 text-sm text-center">
                              <div className="w-[90px] truncate inline-block align-middle">
                                {member.email}
                              </div>
                            </td>
                            <td className=" text-center text-sm border border-gray-300">{member.title}</td>
                            <td className="border border-gray-300 text-center ">
                              <div className='flex justify-center gap-4'>
                                <button className="cursor-pointer" title="Edit" onClick={() => handleEditMember(member)}>
                                  <BiEdit size={25} />
                                </button>
                                <button className="cursor-pointer" title="Delete" onClick={() => {
                                  setMemberToDelete(member);
                                  setShowConfirmModal(true);
                                }}>
                                  <BiTrash size={25} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      }).reverse()}
                      {Members?.length === 0 && (
                        <tr>
                          <td colSpan="5" className="p-4 text-center text-gray-500 border border-gray-300">
                            There is no member
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                </div>
                {/* Create Modal */}
                <Dialog open={showModal} onClose={setShowModal} className="relative z-[9999]">
                  <DialogBackdrop transition className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in" />

                  <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4 text-center sm:items-center sm:p-0">
                      <DialogPanel transition className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full max-w-[70%] data-closed:sm:translate-y-0 data-closed:sm:scale-95" >
                        <DialogTitle className="px-4 pt-5 sm:p-6 sm:pb-4 text-xl font-semibold">Add a member</DialogTitle>
                        <form onSubmit={handleSubmit(onCreateSubmit)}>
                          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div className="sm:col-span-4 mb-3">
                              <label htmlFor="title" className="block text-sm/6 font-medium text-gray-900">
                                Name
                              </label>
                              <div className="mt-2">
                                <input
                                  id="name"
                                  name="name"
                                  type="text"
                                  autoComplete="name"
                                  {...register('name', {
                                    required: 'Name is required',
                                  })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                />
                                {errors.name && (
                                  <span className="error-message">
                                    {errors.name.message}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="sm:col-span-4 mb-3">
                              <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                                Email
                              </label>
                              <div className="mt-2">
                                <input
                                  id="email"
                                  name="email"
                                  type="email"
                                  autoComplete="email"
                                  {...register('email', {
                                    required: 'Email is required',
                                  })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                />
                                {errors.email && (
                                  <span className="error-message">
                                    {errors.email.message}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="sm:col-span-4 mb-3">
                              <label htmlFor="title" className="block text-sm/6 font-medium text-gray-900">
                                Title
                              </label>
                              <div className="mt-2">
                                <input
                                  id="title"
                                  name="title"
                                  type="text"
                                  autoComplete="title"
                                  {...register('title', {
                                    required: 'Title is required',
                                  })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                />
                                {errors.title && (
                                  <span className="error-message">
                                    {errors.title.message}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="sm:col-span-4 mb-3">
                              <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                                Image
                              </label>
                              <div className="mt-2">
                                <Controller
                                  name="file"
                                  control={control}
                                  defaultValue={""} // Initialize with empty array
                                  render={({ field }) => {
                                    const { getRootProps, getInputProps, isDragActive } = useDropzone({
                                      accept: {
                                        'image/jpeg': ['.jpeg', '.jpg'],
                                        'image/png': ['.png']
                                      },
                                      multiple: false,
                                      onDrop: (acceptedFiles) => {
                                        console.log("Files dropped:", acceptedFiles);
                                        if (acceptedFiles.length > 0) {
                                          uploadFiles(acceptedFiles, field);
                                        }
                                      }
                                    });

                                    return (
                                      <>
                                        <div {...getRootProps()} className="dropzone border-2 border-dashed border-gray-300 p-6 mb-4 cursor-pointer hover:border-gray-400 transition-colors">
                                          <input {...getInputProps()} />
                                          <div className="text-center">
                                            {isDragActive ? (
                                              <p className="text-blue-600">Drop the files here ...</p>
                                            ) : (
                                              <div>
                                                <p className="text-gray-600">Drag & drop images here, or click to select</p>
                                                <p className="text-sm text-gray-400 mt-1">Supports: JPG, JPEG, PNG</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {isUploading && (
                                          <div className="mb-4">
                                            <p className="text-blue-600">Uploading files...</p>
                                          </div>
                                        )}
                                        {uploadedFiles && (
                                          <>
                                            {uploadedFiles.length > 0 && (
                                              <div className="mb-4">
                                                <h4 className="font-medium mb-2">Uploaded Files:</h4>
                                                <ul className="space-y-2">
                                                  {
                                                    <li className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                                      <div className='flex items-center gap-2'>
                                                        <img width={100} src={baseUrl + uploadedFiles} className='rounded-full' />
                                                        {/* <span className="text-sm text-gray-700">{uploadedFiles}</span> */}
                                                      </div>
                                                      <button
                                                        type="button"
                                                        onClick={() => removeFile(field)}
                                                        className="text-red-600 hover:text-red-800 text-sm cursor-pointer"
                                                      >
                                                        Remove
                                                      </button>
                                                    </li>
                                                  }
                                                </ul>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </>
                                    );
                                  }}
                                />

                                {errors.file && (
                                  <span className="error-message">
                                    {errors.file.message}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            <button type="submit" className="cursor-pointer inline-flex w-full justify-center rounded-md bg-[#F2A549] px-3 py-2 text-sm font-semibold text-black shadow-xs hover:bg-[#f1c40f] sm:ml-3 sm:w-auto" >
                              Create
                            </button>
                            <button type="button" data-autofocus onClick={handleCloseModal} className="mt-3 cursor-pointer inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto" >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </DialogPanel>
                    </div>
                  </div>
                </Dialog>
                {/* Edit Modal */}
                <Dialog open={showEditModal} onClose={setShowEditModal} className="relative z-[9999]">
                  <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
                  />

                  <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4 text-center sm:items-center sm:p-0">
                      <DialogPanel transition className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full max-w-[70%] data-closed:sm:translate-y-0 data-closed:sm:scale-95" >
                        <DialogTitle className="px-4 pt-5 sm:p-6 sm:pb-4 text-xl font-semibold">Update un membre</DialogTitle>

                        <form onSubmit={handleSubmit(onUpdateSubmit)}>
                          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div className="sm:col-span-4 mb-3">
                              <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                                name
                              </label>
                              <div className="mt-2">
                                <input
                                  id="name"
                                  name="name"
                                  type="text"
                                  autoComplete="name"
                                  {...register('name', {
                                    required: 'name is required',
                                  })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                />
                                {errors.name && (
                                  <span className="error-message">
                                    {errors.name.message}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="sm:col-span-4 mb-3">
                              <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                                Email
                              </label>
                              <div className="mt-2">
                                <input
                                  id="email"
                                  name="email"
                                  type="email"
                                  autoComplete="email"
                                  {...register('email', {
                                    required: 'Email is required',
                                  })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                />
                                {errors.email && (
                                  <span className="error-message">
                                    {errors.email.message}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="sm:col-span-4 mb-3">
                              <label htmlFor="title" className="block text-sm/6 font-medium text-gray-900">
                                Titre
                              </label>
                              <div className="mt-2">
                                <input
                                  id="title"
                                  name="title"
                                  type="text"
                                  autoComplete="title"
                                  {...register('title', {
                                    required: 'Titre is required',
                                  })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                />
                                {errors.title && (
                                  <span className="error-message">
                                    {errors.title.message}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="sm:col-span-4 mb-3">
                              <label htmlFor="images" className="block text-sm/6 font-medium text-gray-900">
                                Image
                              </label>
                              <div className="mt-2">
                                <Controller
                                  name="file"
                                  control={control}
                                  defaultValue={""} // Initialize with empty array
                                  rules={{
                                    required: "Image is required",
                                    validate: (value) => {
                                      if (!value || value.length === 0) {
                                        return "Au moins une image est requise";
                                      }
                                      return true;
                                    }
                                  }}
                                  render={({ field }) => {
                                    const { getRootProps, getInputProps, isDragActive } = useDropzone({
                                      accept: {
                                        'image/jpeg': ['.jpeg', '.jpg'],
                                        'image/png': ['.png']
                                      },
                                      multiple: false,
                                      onDrop: (acceptedFiles) => {
                                        console.log("Files dropped:", acceptedFiles);
                                        if (acceptedFiles.length > 0) {
                                          uploadFiles(acceptedFiles, field);
                                        }
                                      }
                                    });

                                    return (
                                      <>
                                        <div {...getRootProps()} className="dropzone border-2 border-dashed border-gray-300 p-6 mb-4 cursor-pointer hover:border-gray-400 transition-colors">
                                          <input {...getInputProps()} />
                                          <div className="text-center">
                                            {isDragActive ? (
                                              <p className="text-blue-600">Drop the files here ...</p>
                                            ) : (
                                              <div>
                                                <p className="text-gray-600">Drag & drop images here, or click to select</p>
                                                <p className="text-sm text-gray-400 mt-1">Supports: JPG, JPEG, PNG</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {isUploading && (
                                          <div className="mb-4">
                                            <p className="text-blue-600">Uploading files...</p>
                                          </div>
                                        )}

                                        {uploadedFiles !== null && (
                                          <div className="mb-4">
                                            <h4 className="font-medium mb-2">Uploaded Files:</h4>
                                            <ul className="space-y-2">
                                              <li className="flex items-center justify-between bg-gray-50 p-2 rounded" >
                                                <div className="flex items-center gap-2">
                                                  {console.log(uploadedFiles)}
                                                  <img
                                                    width={100}
                                                    src={baseUrl + uploadedFiles}
                                                    alt={selectedMember?.title || "Uploaded image"}
                                                    className="rounded-full"
                                                  />
                                                </div>
                                                <button
                                                  type="button"
                                                  onClick={() => removeFile(field)}
                                                  className="text-red-600 hover:text-red-800 text-sm cursor-pointer"
                                                >
                                                  Remove
                                                </button>
                                              </li>
                                            </ul>
                                          </div>
                                        )}
                                      </>
                                    );
                                  }}
                                />

                                {errors.file && (
                                  <span className="error-message">
                                    {errors.file.message}
                                  </span>
                                )}
                              </div>
                            </div>

                          </div>
                          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            <button
                              type="submit"
                              className="inline-flex w-full justify-center rounded-md bg-[#F2A549] px-3 py-2 text-sm font-semibold text-black shadow-xs hover:bg-[#f1c40f] sm:ml-3 sm:w-auto"
                            >
                              Update
                            </button>
                            <button
                              type="button"
                              data-autofocus
                              onClick={handleCloseEditModal}
                              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </DialogPanel>
                    </div>
                  </div>
                </Dialog>
                <PaginationControls
                  itemsPerPage={pagination.perPage}
                  totalItems={pagination.total}
                  currentPage={pagination.currentPage}
                  paginate={paginate}
                />
              </div>
            )}

          </div>
        </div>

        <ConfirmationModal
          show={showConfirmModal}
          title={memberToDelete ? memberToDelete.name : ''}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </div>

    </DashboardTemplate>
  );
}

export default Member;
